import { PigMountainOverlay, readPmBest } from "../components/PigMountain.jsx";
import { SnackCatchOverlay, WarmupScreen, readBest, warmupDoneToday } from "../components/SnackCatch.jsx";
import { useBackLayer } from "../lib/backstack.js";
import { PM_SPRITE_SRC } from "../lib/pigMountainArt.js";
import { track } from "../lib/metrics.js";
import { arcadeWarmupModeFor } from "../lib/snackCatch.js";
const { useState, useEffect } = React;

/* --------------------------- Piggington's Arcade — the picker (LB-256) --------------------------- */
//
// The arcade holds two games (Snack Attack, Pig Mountain), so the Home card opens this instead
// of Snack Attack directly: today's Brain Warm-Up if it is still owed, then a picker, then the
// chosen game. Leaving a game comes back to the picker; "Back to Home" leaves the arcade.
//
// One warm-up a day and the parent's one Arcade switch cover the WHOLE arcade (Alan, 1 Oct
// 2026): the settings are still learningContent[childId].arcade.{snackCatchEnabled,warmupMode}
// and the warm-up key is still lib/snackCatch.js's — nothing was renamed or migrated, the
// names just predate the second game.
//
// Law 1 posture, as in both games: nothing here imports an earning path, and nothing is
// written to household state. Each game's best is its own device-local key.
//
const NAVY = "#1B2A4A";
const MARIGOLD = "#FFC629";

// The two thumbnails are Claude Design's (2 Oct 2026), symbols in the Pig Mountain sprite.

function Thumb({ id }) {
  return (
    <svg viewBox="0 0 358 120" className="w-full block" aria-hidden="true">
      <use href={`${PM_SPRITE_SRC}#${id}`} width="358" height="120" />
    </svg>
  );
}

// `bestTestid` is passed whole, not built from `testid`: check-spec-selectors.mjs reads src/ for
// the literal ids the specs aim at.
function GameRow({ testid, bestTestid, thumb, name, line, best, onPlay }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden flex flex-col">
      {thumb}
      <div className="px-3.5 pt-3 pb-3.5 flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="text-[17px] leading-6 font-extrabold">{name}</div>
          <div className="text-[13px] leading-[18px] font-medium text-slate-600">{line}</div>
          {best && <div data-testid={bestTestid} className="text-[13px] leading-[18px] font-bold">{best}</div>}
        </div>
        <button data-testid={testid} onClick={onPlay} className="flex-none h-11 px-7 rounded-lg text-[15px] font-extrabold" style={{ background: MARIGOLD, color: NAVY }}>Play</button>
      </div>
    </div>
  );
}

// `scTest`/`scBoss` are Snack Attack's preview-only E2E hooks and `pmTest` is Pig Mountain's;
// all three are resolved (and gated to preview mode) by ChildView and only passed through.
export function ArcadeOverlay({ child, learning, siblings, scTest, scBoss, pmTest, onClose }) {
  const needsWarmup = arcadeWarmupModeFor(learning) === "warmup" && !warmupDoneToday(child.id);
  const [screen, setScreen] = useState(needsWarmup ? "warmup" : "picker");
  const [justUnlocked, setJustUnlocked] = useState(false);
  // W2.5 feature adoption: one arcade_opened per arcade visit, whichever game is then played
  // (it used to fire from SnackCatchOverlay, when that WAS the arcade).
  useEffect(() => {
    track("arcade_opened", {});
  }, []);
  // LB-315: inside a game, the back gesture returns to the picker; from the picker it is
  // ChildView's own arcade layer that closes the whole overlay.
  const inGame = screen === "snack" || screen === "mountain";
  useBackLayer(inGame, () => setScreen("picker"));

  const snackBest = readBest(child.id);
  const pmBest = readPmBest(child.id);

  return (
    <div data-testid="arcade-overlay" className="fixed inset-0 bg-white z-50 flex flex-col" style={{ color: NAVY }}>
      {screen === "warmup" && (
        <WarmupScreen
          child={child}
          learning={learning}
          scTest={scTest}
          onClose={onClose}
          onDone={() => { setJustUnlocked(true); setScreen("picker"); }}
        />
      )}

      {screen === "picker" && (
        <div data-testid="arcade-picker" className="w-full h-full overflow-y-auto">
          <div className="w-full max-w-md mx-auto px-4 pb-6 flex flex-col gap-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
            <button data-testid="arcade-back" onClick={onClose} className="self-start flex items-center gap-1.5 h-11 text-[15px] font-bold">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={NAVY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
              <span>Back to Home</span>
            </button>
            <div className="flex flex-col gap-1">
              <h1 className="text-[22px] leading-7 font-extrabold">Piggington's Arcade</h1>
              {justUnlocked && <div data-testid="arcade-unlocked" className="text-[15px] leading-[22px] font-extrabold text-pink-600">Arcade open! Let's play!</div>}
              <div className="text-[15px] leading-[22px] font-medium text-slate-600">Pick a game to play</div>
            </div>
            <GameRow
              testid="arcade-pick-snack"
              bestTestid="arcade-pick-snack-best"
              thumb={<Thumb id="dd-thumb-snack-attack" />}
              name="Snack Attack"
              line="Catch the snacks, dodge the junk!"
              best={snackBest ? `Your best: ${snackBest.best}` : null}
              onPlay={() => setScreen("snack")}
            />
            <GameRow
              testid="arcade-pick-mountain"
              bestTestid="arcade-pick-mountain-best"
              thumb={<Thumb id="dd-thumb-pig-mountain" />}
              name="Pig Mountain"
              line="Sled as far as you can and race the Greedy Crow"
              best={pmBest ? `Your best: ${pmBest.toLocaleString("en-GB")} m` : null}
              onPlay={() => setScreen("mountain")}
            />
          </div>
        </div>
      )}

      {screen === "snack" && (
        <SnackCatchOverlay child={child} learning={learning} scTest={scTest} scBoss={scBoss} embedded onClose={() => setScreen("picker")} />
      )}

      {screen === "mountain" && (
        <PigMountainOverlay child={child} siblings={siblings} pmTest={pmTest} onClose={() => setScreen("picker")} />
      )}
    </div>
  );
}
